import { FileText, Globe } from 'lucide-react';
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CitationInfo, Document, WebCitationInfo } from '@/utils/citation-utils';

interface CitedDocumentsListProps {
  /** Map of document GUID to array of citation info (number + optional chunk ID) */
  citedDocuments: Map<string, CitationInfo[]>;
  /** Public-web citations, grouped into the same Sources list as case files. */
  webSources?: WebCitationInfo[];
  /** Available documents for name lookup */
  documents?: Document[];
  /** Whether to use compact styling */
  compact?: boolean;
  /** Matter slug for building document URLs */
  slug?: string;
}

const COMPACT_FILENAME_MAX_LENGTH = 64;
const DEFAULT_FILENAME_MAX_LENGTH = 88;
const COMPACT_COLLAPSED_DOCUMENT_COUNT = 3;
const COMPACT_COLLAPSED_CITATION_COUNT = 10;

const formatCompactSourcesSummary = (
  documentCount: number,
  webHostCount: number,
  citationCount: number,
) => {
  const rowCount = documentCount + webHostCount;
  const citationLabel = citationCount === 1 ? 'citation' : 'citations';
  if (webHostCount === 0) {
    const docLabel = documentCount === 1 ? 'doc' : 'docs';
    return `${documentCount} ${docLabel} · ${citationCount} ${citationLabel}`;
  }
  const sourceLabel = rowCount === 1 ? 'source' : 'sources';
  return `${rowCount} ${sourceLabel} · ${citationCount} ${citationLabel}`;
};

type ListedSource =
  | { kind: 'document'; sort: number; guid: string; citations: CitationInfo[] }
  | { kind: 'web'; sort: number; host: string; citations: WebCitationInfo[] };

/**
 * Reusable component for rendering cited documents with clickable citation numbers
 * Used by Case Partner responses and MatterMemory
 */
export const CitedDocumentsList: React.FC<CitedDocumentsListProps> = ({
  citedDocuments,
  webSources = [],
  documents,
  compact = false,
  slug,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const textSize = compact ? 'text-xs' : 'text-sm';
  const iconSize = compact ? 'w-3 h-3' : 'w-4 h-4';
  const fileNameMaxLength = compact ? COMPACT_FILENAME_MAX_LENGTH : DEFAULT_FILENAME_MAX_LENGTH;
  const documentsByGuid = documents ? new Map(documents.map((document) => [document.guid, document])) : undefined;

  const documentRows: ListedSource[] = (Array.from(citedDocuments.entries()) as [string, CitationInfo[]][])
    .filter(([docGuid]) => documentsByGuid?.has(docGuid))
    .map(([guid, citations]) => ({
      kind: 'document' as const,
      sort: parseInt(citations[0]?.displayNumber || '999', 10),
      guid,
      citations,
    }));

  const webByHost = new Map<string, WebCitationInfo[]>();
  for (const source of webSources) {
    const rows = webByHost.get(source.host) ?? [];
    rows.push(source);
    webByHost.set(source.host, rows);
  }
  const webRows: ListedSource[] = Array.from(webByHost.entries()).map(([host, citations]) => ({
    kind: 'web' as const,
    sort: parseInt(citations[0]?.displayNumber || '999', 10),
    host,
    citations,
  }));

  const listed = [...documentRows, ...webRows].sort((a, b) => a.sort - b.sort);
  if (listed.length === 0) return null;

  const citationCount = listed.reduce((total, row) => total + row.citations.length, 0);
  const shouldCollapse = compact && listed.length > COMPACT_COLLAPSED_DOCUMENT_COUNT;
  const visibleRows = shouldCollapse && !isExpanded
    ? listed.slice(0, COMPACT_COLLAPSED_DOCUMENT_COUNT)
    : listed;
  const hiddenRowCount = listed.length - visibleRows.length;

  return (
    <TooltipProvider delayDuration={150}>
      <div className={compact ? 'border-t border-slate-200/80 pt-2' : 'space-y-2.5'}>
        {compact && (
          <div className="mb-1.5 flex min-w-0 flex-nowrap items-center justify-between gap-3">
            <div className="min-w-0 truncate text-[11px] font-medium text-slate-500">
              Sources
            </div>
            <div className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-slate-400">
              {formatCompactSourcesSummary(documentRows.length, webRows.length, citationCount)}
            </div>
          </div>
        )}

        <div className={compact ? 'space-y-1' : 'space-y-2.5'}>
          {visibleRows.map((row) => {
            if (row.kind === 'web') {
              const visibleCitations = compact && !isExpanded
                ? row.citations.slice(0, COMPACT_COLLAPSED_CITATION_COUNT)
                : row.citations;
              const hiddenCitationCount = row.citations.length - visibleCitations.length;

              return (
                <div
                  key={`web:${row.host}`}
                  className={cn(
                    'flex min-w-0 gap-2',
                    compact ? 'items-start py-0.5' : 'items-start'
                  )}
                >
                  <Globe className={cn(iconSize, 'mt-0.5 shrink-0 text-teal-700/70')} />
                  <div className="flex-1 min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                      <span className={`font-medium ${textSize} text-slate-700`}>
                        {row.citations[0]?.siteName || row.host}
                      </span>
                      <span className="shrink-0 text-slate-300">·</span>
                      <span className={`${textSize} flex min-w-0 flex-wrap items-center gap-1 text-slate-500`}>
                        {visibleCitations.map((citation) => (
                          <a
                            key={`${citation.displayNumber}-${citation.url}`}
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="citation-link citation-link-web !ml-0"
                            data-web-url={citation.url}
                            data-file-name={citation.title || citation.host}
                            data-site-name={citation.siteName}
                            title={citation.url}
                          >
                            {citation.displayNumber}
                          </a>
                        ))}
                        {hiddenCitationCount > 0 && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
                            +{hiddenCitationCount}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            }

            const doc = documentsByGuid?.get(row.guid);
            if (!doc) return null;

            const docName = doc?.displayName || doc?.fileName || 'Unknown Document';
            const displayedDocName = truncateFileName(docName, fileNameMaxLength);
            const isTruncated = displayedDocName !== docName;
            const visibleCitations = compact && !isExpanded
              ? row.citations.slice(0, COMPACT_COLLAPSED_CITATION_COUNT)
              : row.citations;
            const hiddenCitationCount = row.citations.length - visibleCitations.length;

            return (
              <div
                key={row.guid}
                className={cn(
                  'flex min-w-0 gap-2',
                  compact ? 'items-start py-0.5' : 'items-start'
                )}
              >
                <FileText className={cn(iconSize, 'mt-0.5 shrink-0 text-slate-400')} />
                <div className="flex-1 min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                    <CitedDocumentName
                      className={`font-medium ${textSize} text-slate-700`}
                      displayedName={displayedDocName}
                      fullName={docName}
                      isTruncated={isTruncated}
                    />
                    <span className="shrink-0 text-slate-300">·</span>
                    <span className={`${textSize} flex min-w-0 flex-wrap items-center gap-1 text-slate-500`}>
                      {visibleCitations.map((citation) => {
                        const {
                          displayNumber,
                          chunkId,
                          segmentIndex,
                          cellRange,
                        } = citation;

                        let href: string;
                        if (segmentIndex !== undefined) {
                          href = slug
                            ? `/matter/${slug}/f/${row.guid}?from=overview&segment=${segmentIndex}${cellRange ? `&cellRange=${encodeURIComponent(cellRange)}` : ''}`
                            : `#cite-${row.guid}-s${segmentIndex}${cellRange ? `-${cellRange}` : ''}`;
                        } else if (chunkId !== undefined) {
                          href = slug
                            ? `/matter/${slug}/f/${row.guid}?from=overview&chunk=${chunkId}${cellRange ? `&cellRange=${encodeURIComponent(cellRange)}` : ''}`
                            : `#cite-${row.guid}-${chunkId}${cellRange ? `-${cellRange}` : ''}`;
                        } else {
                          href = slug
                            ? `/matter/${slug}/f/${row.guid}?from=overview&cite=${displayNumber}`
                            : `#cite-${displayNumber}`;
                        }

                        return (
                          <a
                            key={`${displayNumber}-${segmentIndex !== undefined ? `s${segmentIndex}` : chunkId ?? 'doc'}`}
                            href={href}
                            className="citation-link !ml-0"
                            data-cite={chunkId === undefined && segmentIndex === undefined ? displayNumber : undefined}
                            data-chunk-id={chunkId !== undefined ? chunkId.toString() : undefined}
                            data-segment-index={segmentIndex !== undefined ? segmentIndex.toString() : undefined}
                            data-cell-range={cellRange}
                            data-doc-guid={row.guid}
                            title={docName}
                          >
                            {displayNumber}
                          </a>
                        );
                      })}
                      {hiddenCitationCount > 0 && (
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
                          +{hiddenCitationCount}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {shouldCollapse && (
          <button
            type="button"
            onClick={() => setIsExpanded((value) => !value)}
            className="mt-1.5 text-[11px] font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            {isExpanded ? 'Show fewer sources' : `Show ${hiddenRowCount} more source${hiddenRowCount === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
    </TooltipProvider>
  );
};

function CitedDocumentName({
  className,
  displayedName,
  fullName,
  isTruncated,
}: {
  className: string;
  displayedName: string;
  fullName: string;
  isTruncated: boolean;
}) {
  const label = (
    <span className={`${className} inline-block max-w-full min-w-0 truncate align-bottom`}>
      {displayedName}
    </span>
  );

  if (!isTruncated) return label;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{label}</TooltipTrigger>
      <TooltipContent className="max-w-xs px-2.5 py-2">
        <p className="break-all text-[11px] text-gray-700 leading-relaxed">{fullName}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function truncateFileName(fileName: string, maxLength: number) {
  if (fileName.length <= maxLength) return fileName;

  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === fileName.length - 1) {
    return `${fileName.slice(0, Math.max(8, maxLength - 3))}...`;
  }

  const extension = fileName.slice(lastDot);
  const nameWithoutExtension = fileName.slice(0, lastDot);
  const truncatedBaseLength = Math.max(8, maxLength - extension.length - 3);
  return `${nameWithoutExtension.slice(0, truncatedBaseLength)}...${extension}`;
}
